import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSchedule } from '../context/ScheduleContext';
import { resolveActiveDay, parseMinutes, formatTime12, formatLocation, formatShortDate } from '../utils/schedule';
import './TodayScheduleWidget.css';

const ClassIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const TodayScheduleWidget = () => {
  const { meetings, loading, error, refetch } = useSchedule();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const active = useMemo(() => resolveActiveDay(meetings, now), [meetings, now]);

  const nextId = useMemo(() => {
    if (!active || !active.isToday) return null;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const upcoming = active.meetings.find((m) => (parseMinutes(m.end_time) ?? 0) > nowMinutes);
    return upcoming ? upcoming.id : null;
  }, [active, now]);

  const isPast = (m) => {
    if (!active || !active.isToday) return false;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return (parseMinutes(m.end_time) ?? 0) <= nowMinutes;
  };

  return (
    <section className="today-schedule" aria-labelledby="today-schedule-heading">
      <div className="today-schedule-head">
        <h2 id="today-schedule-heading"><ClassIcon /> Classes</h2>
        {active && <span className="today-schedule-day">{active.label} · {formatShortDate(active.date)}</span>}
      </div>

      {loading ? (
        <div className="today-schedule-state">
          <div className="loading-spinner" />
          <p>Loading your classes…</p>
        </div>
      ) : error ? (
        <div className="today-schedule-state today-schedule-state--error">
          <p>Couldn't load your classes.</p>
          <button className="btn-retry" onClick={refetch}>Try Again</button>
        </div>
      ) : !active ? (
        <div className="today-schedule-state">
          <p>No upcoming classes.</p>
        </div>
      ) : (
        <ul className="today-schedule-list">
          {active.meetings.map((m) => (
            <li
              key={m.id}
              className={`today-schedule-item${isPast(m) ? ' is-past' : ''}${m.id === nextId ? ' is-next' : ''}`}
            >
              <div className="today-schedule-main">
                <div className="today-schedule-top">
                  <span className="today-schedule-code">{m.course_code}</span>
                  <div className="today-schedule-time"><ClockIcon /> {formatTime12(m.start_time)} – {formatTime12(m.end_time)}</div>
                </div>
                {m.course_name && <span className="today-schedule-name">{m.course_name}</span>}
                <span className="today-schedule-loc"><LocationIcon /> {formatLocation(m.building, m.room)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Link to="/schedule" className="today-schedule-link">View full schedule</Link>
    </section>
  );
};

export default TodayScheduleWidget;
